import React, { useEffect, useMemo } from 'react'
import { Alert, Col, Form, Row } from 'antd'
import { Formik, useFormikContext } from 'formik'
import { useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import moment from 'moment'
import { formElementPropsGenerator } from '../../../lib/forms'
import { DatePicker, Input, SubmitButton } from '../../Forms'

import api from '../../../api'

import PatientSelect from '../../Forms/PatientSelect'
import DeviceModelSelect from '../../Forms/DeviceModelSelect'

const TRANSMISSIONS_DEFAULTS = {
  patientId: undefined,
  firstName: '',
  lastName: '',
  dateOfBirth: undefined,
  deviceModelId: '',
  deviceSerialNumber: '',
  transmissionDate: moment(),
}

const TRANSMISSIONS_LABELS = {
  patientId: 'Patient',
  firstName: 'First Name',
  lastName: 'Last Name',
  dateOfBirth: 'Date of Birth',
  deviceModelId: 'Model #',
  deviceSerialNumber: 'Serial Number',
  cptCode: 'CPT Code',
  icd10Code: 'ICD-10 Code',
  authorized: 'Authorized',
  practiceMrn: 'Practice MRN',
  transmissionDate: 'Transmission Date',
}

const propsFor = formElementPropsGenerator({
  formName: 'transmission-form',
  labels: TRANSMISSIONS_LABELS,
})

const labelCol = {
  sm: { span: 6, offset: 0 },
}

const wrapperCol = {
  sm: { span: 15 },
}

const actionsWrapperCol = {
  sm: { span: 4, offset: 10 },
}

const buildValidationSchema = () => {
  const shape = {
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    dateOfBirth: Yup.date()
      .max(new Date())
      .required(),
    deviceModelId: Yup.string().required(),
    deviceSerialNumber: Yup.string().required(),
    transmissionDate: Yup.date().required(),
  }

  return Yup.object().shape(shape)
}

const CustomPatientSelector = ({ practiceId, paramsPatientId }) => {
  const { values, setValues } = useFormikContext()

  const { patientId } = values
  const id = paramsPatientId || patientId

  useEffect(() => {
    if (id) {
      api.patients.get(id).then(response => {
        setValues({
          ...TRANSMISSIONS_DEFAULTS,
          patientId: response.id,
          firstName: response.firstName,
          lastName: response.lastName,
          dateOfBirth: moment(response.birthDate),
          practiceMrn: response.practiceMrn,
          deviceModelId: response.currentDevice?.modelName,
          deviceSerialNumber: response.currentDevice?.serialNumber,
        })
      })
    } else {
      setValues({ ...TRANSMISSIONS_DEFAULTS })
    }
  }, [id, setValues])
  if (paramsPatientId) {
    return <></> // hide patient selector if patientId is in the URL
  }

  return (
    <PatientSelect
      {...propsFor('patientId')}
      practiceId={practiceId}
      placeholder="Search by name, DOB or serial number"
      allowClear
      showInactive
    />
  )
}

export default function PatientForm({ practiceId, onSubmit }) {
  const validationSchema = useMemo(() => buildValidationSchema(), [])
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const paramsPatientId = searchParams.get('patientId')

  return (
    <>
      <Formik
        initialValues={TRANSMISSIONS_DEFAULTS}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, isSubmitting, submitCount, values }) => (
          <Form
            onSubmit={handleSubmit}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
          >
            <Alert
              message="If no patient exists, completing the form below with patient information will automatically create a new one."
              type="info"
              showIcon
            />

            <CustomPatientSelector
              practiceId={practiceId}
              paramsPatientId={paramsPatientId}
            />

            <Row>
              <Col span={24}>
                <Input
                  {...propsFor('firstName')}
                  disabled={!!values.patientId}
                />
              </Col>
              <Col span={24}>
                <Input
                  {...propsFor('lastName')}
                  disabled={!!values.patientId}
                />
              </Col>
              <Col span={24}>
                <DatePicker
                  {...propsFor('dateOfBirth')}
                  disabled={!!values.patientId}
                  showToday={false}
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Input
                  {...propsFor('practiceMrn')}
                  disabled={!!values.patientId}
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <DeviceModelSelect
                  {...propsFor('deviceModelId')}
                  labelCol={{ span: 6 }}
                  labelAlign="right"
                  wrapperCol={{ span: 15 }}
                  disabled={!!values.patientId}
                />
              </Col>
              <Col span={24}>
                <Input
                  {...propsFor('deviceSerialNumber')}
                  disabled={!!values.patientId}
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <DatePicker
                  {...propsFor('transmissionDate')}
                  defaultValue={moment()}
                />
              </Col>
            </Row>

            <SubmitButton
              isSubmitting={isSubmitting}
              title="Create Transmission"
              isValid
              submitCount={submitCount}
              wrapperCol={actionsWrapperCol}
            />
          </Form>
        )}
      </Formik>
    </>
  )
}

CustomPatientSelector.propTypes = {
  practiceId: PropTypes.string,
  paramsPatientId: PropTypes.string,
}

CustomPatientSelector.defaultProps = {
  practiceId: undefined,
  paramsPatientId: undefined,
}

PatientForm.propTypes = {
  practiceId: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
}

PatientForm.defaultProps = {
  practiceId: undefined,
}
